'use client';

import { useEffect, useRef, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { FeatherAlertCircle, FeatherCheck } from '@subframe/core';
import { selectNegociacaoDetailConversaId } from '@/features/negociacaoDetalhes';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchMinhaAvaliacao, registrarAvaliacao, removerAvaliacao } from '../store/actions';
import { selectFeedbackByConversa } from '../store/selectors';
import type { FeedbackTipo } from '../types/negociacaoFeedbackTypes';
import { ThumbIcon } from './ThumbIcon';
const FEEDBACK_OPTIONS: {
  tipo: FeedbackTipo;
  label: string;
  text: string;
}[] = [{
  tipo: 'positive',
  label: 'Dar feedback positivo',
  text: 'Foi útil'
}, {
  tipo: 'negative',
  label: 'Dar feedback negativo',
  text: 'Pode melhorar'
}];
type FeedbackToast = {
  text: string;
  tone: 'success' | 'error';
};
export function FeedbackButton() {
  const dispatch = useAppDispatch();
  const conversaId = useAppSelector(selectNegociacaoDetailConversaId);
  const feedback = useAppSelector(selectFeedbackByConversa(conversaId));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<FeedbackToast | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (conversaId) {
      dispatch(fetchMinhaAvaliacao(conversaId));
    }
  }, [dispatch, conversaId]);
  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);
  const showToast = (next: FeedbackToast) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(next);
    toastTimer.current = setTimeout(() => setToast(null), 2500);
  };
  const handleClick = async (tipo: FeedbackTipo) => {
    if (!conversaId || feedback.isLoading || feedback.isMutating || isSubmitting) return;
    const jaSelecionado = feedback.tipo === tipo;
    setIsSubmitting(true);
    try {
      if (jaSelecionado) {
        await dispatch(removerAvaliacao(conversaId)).unwrap();
        showToast({
          text: 'Feedback removido',
          tone: 'success'
        });
      } else if (feedback.tipo) {
        const tipoAnterior = feedback.tipo;
        const justificativaAnterior = feedback.justificativa;
        // O PUT do core só sobrescreve a justificativa: com outro tipo já gravado ele responde
        // 409 e exige remover antes. Daí o DELETE + PUT, com a anterior regravada se o PUT falhar.
        await dispatch(removerAvaliacao(conversaId)).unwrap();
        try {
          await dispatch(registrarAvaliacao({
            conversaId,
            tipo,
            justificativa: null
          })).unwrap();
          showToast({
            text: 'Feedback atualizado',
            tone: 'success'
          });
        } catch {
          try {
            await dispatch(registrarAvaliacao({
              conversaId,
              tipo: tipoAnterior,
              justificativa: justificativaAnterior
            })).unwrap();
            showToast({
              text: 'Não foi possível alterar. Avaliação anterior mantida.',
              tone: 'error'
            });
          } catch {
            await dispatch(fetchMinhaAvaliacao(conversaId));
            showToast({
              text: 'Não foi possível alterar. Confira sua avaliação.',
              tone: 'error'
            });
          }
        }
      } else {
        await dispatch(registrarAvaliacao({
          conversaId,
          tipo,
          justificativa: null
        })).unwrap();
        showToast({
          text: 'Feedback registrado',
          tone: 'success'
        });
      }
    } catch {
      showToast({
        text: jaSelecionado ? 'Erro ao remover feedback' : 'Erro ao alterar feedback',
        tone: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  if (!conversaId) return null;
  return <div className="relative flex w-full flex-wrap items-center justify-between gap-2 rounded-[10px] border border-neutral-200 bg-white px-3 py-1.5">
      {toast && <div role="status" className="absolute right-0 bottom-full mb-2 flex items-center gap-2 rounded-[8px] border border-neutral-200 bg-white px-3 py-2 text-xs shadow-sm">
          {toast.tone === 'error' ? <FeatherAlertCircle className="text-error-600 size-4" /> : <FeatherCheck className="text-success-600 size-4" />}
          <span className="text-default-font">{toast.text}</span>
        </div>}
      <div className="text-default-font flex min-w-0 items-center gap-2 text-xs font-semibold">
        <MessageCircle className="text-brand-700 size-3.5 shrink-0" aria-hidden="true" />
        <span>Como foi a conversa?</span>
      </div>
      <div role="group" aria-label="Avaliação da conversa" className="flex flex-wrap gap-1.5">
        {FEEDBACK_OPTIONS.map(({
        tipo: tipo_0,
        label,
        text
      }) => {
        const selected = feedback.tipo === tipo_0;
        return <button key={tipo_0} type="button" aria-label={label} aria-pressed={selected} disabled={feedback.isLoading || feedback.isMutating || isSubmitting} onClick={() => handleClick(tipo_0)} className={`focus-visible:outline-brand-600 flex min-h-8 items-center justify-center gap-1.5 rounded-[8px] border px-2.5 py-1 text-xs font-semibold transition-colors focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-60 ${selected ? tipo_0 === 'positive' ? 'border-success-200 bg-success-50 text-success-700' : 'border-error-200 bg-error-50 text-error-700' : 'text-subtext-color hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700 border-neutral-200 bg-white'}`}>
              <ThumbIcon tipo={tipo_0} filled={selected} className="size-3.5" />
              {text}
            </button>;
      })}
      </div>
    </div>;
}